import { Component, Entity, System, registerComponent, registerSystem, DetailEvent } from "aframe";
import { Quaternion, QuaternionLike, RepeatWrapping, Vector3, Vector3Like } from "three";
import { RTCClient, myself, themselves, RemoteMediaStreamTrack } from "../../rtc/client";
import { Guid, parseGuid } from "../../rtc/guid";
import { MessageData, ProtocolMessage } from "../../rtc/protocol";
import { AmmoRigidBody, AmmoCollisionObject, AmmoBodyComponent } from "../ammo";
import { AnchorEntity } from "../anchor";
import { AvatarEntity, AvatarSystem } from "../avatar";
import { SlotEntity, SlotEvent } from "../slot";
import { whenLoaded } from "../utils";
import { Variables, VariablesChangedEvent } from "../variables";
import { RtcAvatarEntity, RtcAvatarSystem, RtcAvatarComponent } from "./rtc-avatar";
import { RtcChannelEntity, RtcChannelSystem } from "./rtc-channel";



export interface RtcEntity extends Entity< { [attrName : string] : Component } & {
	"rtc-entity" : RtcEntityComponent,
	"template" : Component 
} >
{
	rtc : RtcEntityComponent;
	ready : Promise<any>;
}

export interface RtcEntityEventDetail
{
	time : number;
	emitter : Guid;
}

export interface RtcEntityData
{
	id : Guid; //The entity's ID
	template : Guid; //The entity's template
	owner : RtcChannelEntity | RtcAvatarEntity | AvatarEntity;
}

export interface RtcEntityComponent extends Component
{
	el : RtcEntity;
	data : RtcEntityData;
	locationPending? : string;

	playLocalTrack(track:MediaStreamTrack) : RtcEntityComponent;
	sendTransform() : RtcEntityComponent;
	sendBindings() : RtcEntityComponent;
	sendVariables(changed : Variables) : RtcEntityComponent;
	attachTo(parent : SlotEntity | null) : RtcEntityComponent;
}

interface RtcEntityComponentInternal extends RtcEntityComponent
{
	el : RtcEntity;
	system : RtcEntitySystemInternal;
	
	physics : Promise< AmmoRigidBody | null >;

	//Transform stuff :
	position : Vector3;
	rotation : Quaternion;

	//Listeners, undefined until they can be added
	attachedListener : EventListener;
	detachedListener : EventListener;
	variablesChangedListener? : EventListener;

	processMessage(code : number, emitter : Guid, time : number, payload : Uint8Array) : void;
	playTrack(track:MediaStreamTrack) : void;
	
	
}


interface ControlCodes
{
	Create : ProtocolMessage;
	Attach : ProtocolMessage;
	//Attributes : ProtocolMessage;
	Transform : ProtocolMessage;
	Cast : ProtocolMessage;
	//Bindings : ProtocolMessage;
	Variables : ProtocolMessage;
	Forget : ProtocolMessage;
	Destroy : ProtocolMessage;
}

interface CreatePayload
{
	channelId : Guid;
	templateId : Guid;
}
interface AttachPayload
{
	containerId : Guid;
	slotName : string;
	anchorName : string;
}
interface AttributesPayload
{
	items : {
		attribute : string;
		properties : {
			property : string;
			value : string;
		}[];
	}[];
}

interface TransformPayload
{
	position : Vector3Like;
	rotation : QuaternionLike;
	linearVelocity : Vector3Like;
	angularVelocity : Vector3Like;
}

interface VariablesPayload
{
	items : {
		name : string;
		type : number;
		"number"? : number;
		"string"? : string;
		"guid"? : Guid;
	}[];
}
const variablesTypes = 
{
	"null" : 0,
	"false" : '0'.charCodeAt(0),
	"true" : '1'.charCodeAt(0),
	"number" : 'N'.charCodeAt(0),
	"string" : 'S'.charCodeAt(0),
	"guid" : 'G'.charCodeAt(0)
};

interface CastPayload
{
	mid : string;
}

export interface RtcEntityCreationOptions
{
	transform? : {
		position? : { x:number, y:number, z:number },
		rotation? : { x:number, y:number, z:number, w:number } 
	};
	attach? : { to? : Guid|string, slot? : string, anchor? : string };
	variables? : { [key : string] : string|number|boolean };
};

export interface RtcEntitySystem extends System
{
	createEntityAsync(templateId : Guid | string, channel? : RtcChannelEntity, options? : RtcEntityCreationOptions ) : Promise<RtcEntity>;
	getEntityAsync(id:Guid) : Promise<RtcEntity>;
}

interface RtcEntitySystemInternal extends RtcEntitySystem
{
	data : {
		endpoint : string;
	};
	canPlayMedia : Promise<void>;
	client? : RTCClient;
	protocol? : ControlCodes;
	entities : { [id : string ] : RtcEntity };
	processMessage(data : MessageData) : void;
}

registerSystem("rtc-entity", {

	schema : {
		endpoint : { type : "string" }
	},
	
	init(this : RtcEntitySystemInternal)
	{
		this.entities = {};


		this.el.addEventListener("rtc-connected", (event) =>
		{
			this.client = (event as DetailEvent<RTCClient>).detail;


			const codes : { [name:string] : ProtocolMessage } = {};
			for(let pm of this.client.protocol!.entity)
				codes[pm.name] = pm;
			this.protocol = codes as any;
			
			const firstCode = codes.Create!.code, lastCode = codes.Destroy!.code;

			const onMessage = (event : DetailEvent<MessageData>) =>
				(event.detail.code >= firstCode) && (event.detail.code <= lastCode)
				&& this.processMessage(event.detail);

			this.client.addEventListener("message", onMessage as EventListener);
		});
	},


	createEntityAsync(this : RtcEntitySystemInternal, templateId : Guid | string, channel? : RtcChannelEntity, options? : RtcEntityCreationOptions) : Promise<RtcEntity>
	{
		return new Promise<RtcEntity>( async (resolve, reject) => {

			const body : any = {
				templateId : templateId.toString()
			};
			if(channel)
				body.channelId = channel.components["rtc-channel"].data.id.toString();
			if(options)
			{
				if(options.transform)
				{
					body.transform = { 
						position : options.transform.position || { x:0, y:0, z:0 }
					};
					const r = options.transform.rotation;
					if(r && (r.w < 1.0) && (r.w > -1.0)) //Non null, non identity
					{
						const k = 1.0 / (1.0 - r.w*r.w);
						body.transform.rotation = {
							angle : 2.0 * Math.acos(r.w),
							axis : { x : r.x * k, y : r.y * k, z : r.z * k }
						};
					}
				}
				
				if(options.attach)
					body.attach = options.attach.to ? {  
						to : options.attach.to.toString(),
						slot : options.attach.slot,
						anchor : options.attach.anchor
					} : { };
			}
			

			const request = {
				method : "POST",
				headers : { "Content-Type" : "application/json" },
				body : JSON.stringify(body)
			};
			this.el.emit("authenticate-request", request);

			const response = await fetch(this.data.endpoint, request);

			const entityId = parseGuid( (await response.json()).id );

			resolve( await this.getEntityAsync(entityId!) );
		} );
	},

	getEntityAsync(this : RtcEntitySystemInternal, id : Guid) : Promise<RtcEntity>
	{
		return new Promise<RtcEntity>((resolve, reject) =>
		{
			const e = this.entities[ id.toString() ];
			if(e)
				return resolve(e);

			const listener = (event:Event) => {
				const entity = event.target as RtcEntity;
				if(id.equals( (event.target as RtcEntity).components["rtc-entity"].data.id ))
				{
					resolve(entity);
					this.el.removeEventListener("rtc-entity-created", listener);
				}
			};
			this.el.addEventListener("rtc-entity-created", listener);
		});
	},


	processMessage(this : RtcEntitySystemInternal, message : MessageData) : void
	{
		const entityGuid = new Guid(message.data);
		const entityId = entityGuid.toString();

		const codes = this.protocol!;
		const payload = message.data.subarray(16);

		let entity : RtcEntity | null = null;
		if(message.code == codes.Create.code) //Created a new entity
		{
			entity = (this.el.ownerDocument.createElement("a-entity") as RtcEntity)!;
			entity.id = entityId;

			// Entity ready = "template__rtc-entity" loaded.
			entity.ready = new Promise( (resolve, reject) =>
			{
				const listener = ((event : DetailEvent< { component : Component } >) =>
				{
					if(event.detail.component.id != "rtc-entity" ) return;
					entity!.removeEventListener("template-loaded", listener);

					if(entity!.hasLoaded)
						resolve(event);
					else
						entity!.addEventListener("loaded", resolve, { once : true } );
				}) as EventListener;
				entity!.addEventListener("template-loaded", listener);
			});
			
			const data = this.client!.deserialize(codes.Create, payload) as CreatePayload;
			const mine = message.emitter.equals(myself);

			let owner : RtcChannelEntity | RtcAvatarEntity | AvatarEntity;
			let parentEl : Entity;

			if(!data.channelId.equals(Guid.empty)) //New Entity created inside a channel
			{
				const channelSystem = this.el.systems["rtc-channel"] as RtcChannelSystem;
				const channelEl = channelSystem.getChannel(data.channelId);
				if(!channelEl)
					return console.warn("Unknown channel", data.channelId);
				
				owner = channelEl;
				parentEl = channelEl;
			}
			else if(mine) //New Entity owned by myself
			{
				const avatarSystem = this.el.systems["avatar"] as AvatarSystem;
				owner = avatarSystem.avatar;
				parentEl = owner.querySelector<Entity>("[avatar-inventory]") || owner;
			}
			else //New Entity owned by its creator. This will only be sent when the entity is attached.
			{
				const avatarSystem = this.el.systems["rtc-avatar"] as RtcAvatarSystem;
				const avatarEl = avatarSystem.getAvatar(message.emitter);
				owner = avatarEl;
				parentEl = avatarEl.querySelector<Entity>("[avatar-inventory]") || avatarEl;
			}

			const entityData : RtcEntityData = {
				id : entityGuid,
				template : data.templateId,
				owner
			};
			entity.setAttribute("rtc-entity", entityData);
			parentEl.append(entity);

			this.entities[entityId] = entity;
		}
		else
			entity = this.entities[entityId];

		if( !entity )
			return console.warn("Unknown RtcEntity", entityId);

		entity.ready.then( () =>
		{
			if(!(entity && entity.isConnected)) return; //Lost.

			const component = entity!.components["rtc-entity"] as RtcEntityComponentInternal;
			component?.processMessage(message.code, message.emitter, message.time, payload);
			if(message.code >= codes.Forget.code) //Lost contact with the entity
			{
				entity!.remove();
				delete this.entities[entityId];
			}
		});
	},

	//Not sure this still works...
	/* createEntity(this:  RtcEntitySystemInternal, channel : Guid, templateId : Guid ) : Promise< RtcEntity >
	{
		return new Promise< RtcEntity >( (y, n) =>
		{
			if(!this.client) return n("TODO wait for client connected");

			const messageTime = this.client.sendMessage(this.protocol!.Create, channel.asBytes(), templateId.asBytes() );
			const listener = (e : DetailEvent<RtcEntityEventDetail> & { target : RtcEntity } ) =>
			{
				if( !e.detail.emitter.equals(myself) )
					return;
				if( e.detail.time != messageTime ) 
					return;
				this.el.removeEventListener("rtc-entity-created", listener as EventListener);
				y(e.target);
			};
			this.el.addEventListener("rtc-entity-created", listener as EventListener);
		} );
	}
	*/

} );




const isDynamic = (body : AmmoCollisionObject) => (body.getCollisionFlags() & 0x03) == 0; //Not (static or kinematic) = dynamic

registerComponent("rtc-entity", {
	schema : {
		id : Guid.schema,
		template : Guid.schema
	},
	init(this : RtcEntityComponentInternal)
	{
		const el = this.el;

		this.el.rtc = this;
		this.locationPending = "Attached to another entity";

		this.position = el.object3D.position.clone();
		this.rotation = el.object3D.quaternion.clone();
		el.object3D.visible = false; //Hidden until Attach is known (Transform comes first)


		this.attachedListener = ( (event : SlotEvent) =>
		{
			if(!el.isConnected) return console.warn("rtc-entity is not connected");
			if(event.detail.item !== el) return;
			console.log("Rtc-Entity attached, sending..." );
			this.attachTo( event.detail.slot ).sendTransform(); 
		}) as EventListener;
		
		this.detachedListener = ( (event : SlotEvent) =>
		{
			if(!el.isConnected) return console.warn("rtc-entity is not connected");
			if(event.detail.item !== el) return;
			console.log("Rtc-Entity detached, sending..." );
			this.attachTo( null ).sendTransform(); 
		}) as EventListener;

		el.addEventListener("attached-to-slot", this.attachedListener);
		el.addEventListener("detached-from-slot", this.detachedListener);


		// wait for ready (= template loaded) to listen to variables changes
		this.el.ready.then( () => {
			this.variablesChangedListener = ( (event : VariablesChangedEvent) =>
			{
				if(!el.isConnected) return console.warn("rtc-entity is not connected");
				this.sendVariables(event.detail);
			}) as EventListener;
			el.addEventListener("variables-changed", this.variablesChangedListener);
		} );

		this.physics = (async () =>
		{
			await this.el.ready;
			const abc = el.components["ammo-body"] as AmmoBodyComponent;
			return abc ? (await abc.bodyInitialized) : null;
		})();



		el.addEventListener("play-media-track", ((event : DetailEvent<MediaStreamTrack>) =>
		{
			if( (event.detail as RemoteMediaStreamTrack).sourceId )
				return;

			const rtcClient = this.system.client!;
			//Add the local track to the connection, this will trigger a negociation
			rtcClient.addLocalTrack(event.detail).then( (mid) =>
			{
				//Once the track is added and its Mid is known, send a Play message
				rtcClient.sendObjectMessage(this.system.protocol!.Cast, this.data.id, { mid } )
			} );

		} ) as EventListener, { capture : true } );



		/*
		const captureUntilAttached = (event : Event) =>
		{
			event.preventDefault();
			event.stopPropagation();
			el.addEventListener("rtc-entity-attached",
				() => (event.target as Entity).emit(event.type, (event as CustomEvent).detail, event.bubbles ),
				{ once : true } );
		};
		el.addEventListener("gateway-initialized", captureUntilAttached, { capture : true } );
		*/

		//The first Attach code is the signal that the Entity is ready to display
		el.addEventListener("rtc-entity-attached", async (event) =>
		{
			delete this.locationPending; // = false;
			el.emit('location-initialized', this, false);

			//el.removeEventListener("gateway-initialized", captureUntilAttached, { capture : true } );
			el.object3D.visible = true;
			if( await this.physics )
				el.setAttribute("ammo-body", { enabled : true });
		}, { once : true } );



		// Start loading the template. It will trigger ready Promise
		el.setAttribute("template__rtc-entity", this.data.template.toString() );
	},

	remove(this : RtcEntityComponentInternal) { 
		if(this.variablesChangedListener)
			this.el.removeEventListener("variables-changed", this.variablesChangedListener);
		this.el.removeEventListener("attached-to-slot", this.attachedListener);
		this.el.removeEventListener("detached-from-slot", this.detachedListener);	
	},

	processMessage(this : RtcEntityComponentInternal, code : number, emitter : Guid, time : number, payload : Uint8Array) : void
	{
		const protocol = this.system!.protocol!, el = this.el, client = this.system.client!;
		const obj = el.object3D;

		if( code >= protocol.Forget.code )
		{
			obj.visible = false;
		}

		if( code == protocol.Create.code ) //Created
		{
			return el.emit("rtc-entity-created", this);
		}

		//const reader = new Reader(message.data);
		
		if( code == protocol.Transform.code ) //Transform
		{
			const transform = 
			{
				position : this.position,
				rotation : this.rotation,
				linearVelocity : {x:0,y:0,z:0},
				angularVelocity : {x:0,y:0,z:0}
			};
			client.deserialize(protocol.Transform, payload, null, transform);

			obj.position.copy(this.position);
			obj.quaternion.copy(this.rotation);
			obj.updateWorldMatrix(true, false);

			this.physics.then( (body) =>
			{
				if(body && isDynamic(body))
				{	
					const abc = el.components["ammo-body"] as AmmoBodyComponent;
					if(!abc) return console.warn("Entity has a body but no ammo-body");

					console.log("Moving entity's body to", [this.position.x, this.position.y, this.position.z].map(v => v.toFixed(2)).join(";") );
					abc.moveTo(this.position, this.rotation);
					body.getLinearVelocity().setValue(transform.linearVelocity.x, transform.linearVelocity.y, transform.linearVelocity.z);
					body.getAngularVelocity().setValue(transform.angularVelocity.x, transform.angularVelocity.y, transform.angularVelocity.z);
					body.activate(true);
				}
			} );

			return el.emit("rtc-entity-transformed");
		}

		if( code == protocol.Cast.code ) //Play a remote stream
		{
			const rtcClient = this.system.client!;

			const data = client.deserialize(protocol.Cast, payload) as CastPayload;

			rtcClient.getForwardedTrack(emitter, data.mid).then(  (track) =>
			{
				el.emit("play-media-track", track, true);
			});

			return;
			/* TODO
			
			const sourceMid = reader.readString();
			rtcClient.getForwardedTrack(message.emitter, sourceMid).then( (track) => this.playTrack(track) );
			return el.emit("rtc-entity-stream");
			*/
		}

		if( code == protocol.Attach.code ) //Attach to a slot from the emitter
		{
			const data = client.deserialize(protocol.Attach, payload) as AttachPayload;
			//const slotOwner = reader.readGuid();
			
			if(!data.containerId.equals(Guid.empty))
			{
				const rtcAvatarSystem = this.system.el.systems["rtc-avatar"] as RtcAvatarSystem;
				const localAvatarSystem = this.system.el.systems["avatar"] as AvatarSystem;
				let ownerEl : RtcEntity | AvatarEntity | RtcAvatarEntity | null = null;

				
				if(data.containerId.equals(myself))
				{
					//Entity gets attached to a slot on local avatar
					ownerEl = localAvatarSystem.avatar;
				} 
				else if(data.containerId.equals(themselves))
				{
					//Entity gets attached to a slot on the message's emitter
					if(emitter.equals(myself))
						ownerEl = localAvatarSystem.avatar;
					else
						ownerEl = rtcAvatarSystem.getAvatar(emitter);
				}
				else //another Entity or Avatar
				{
					const ownerId = data.containerId.toString();
					ownerEl = this.system.entities[ownerId] || rtcAvatarSystem.avatars[ownerId];
				}

				if(!ownerEl)
					return console.warn("Not found :", data.containerId.toString());

				ownerEl.hasLoaded || console.log("Owner entity has not loaded yet");

				whenLoaded( ownerEl, async () =>
				{
					const ready = (ownerEl as RtcEntity)?.ready;
					if(ready)
						await ready;

					let slot : SlotEntity | null = null;
					const slots = ownerEl!.querySelectorAll<SlotEntity>("[slot]");
					for(let n=slots.length; --n >= 0; )
						if( slots[n].components?.slot?.data?.name == data.slotName )
						{
							slot = slots[n];
							break;
						}

					if(!slot)
					{
						slot = await new Promise<SlotEntity>( (resolve, reject) =>
						{
							const listener = (event : DetailEvent<string>) =>
							{
								if(event.detail != data.slotName) return;
								ownerEl!.removeEventListener("slot-initialized", listener as EventListener);
								resolve(event.target as SlotEntity);
							};
							ownerEl!.addEventListener("slot-initialized", listener as EventListener);
						});
					}

					const anchors = Array.from( el.querySelectorAll<AnchorEntity>("[anchor]") );
					if(el.components.anchor)
						anchors.unshift(el as Entity as AnchorEntity);
					
					let anchor : AnchorEntity | null = null;
					for(let a of anchors)
					{
						if(!a.hasLoaded)
							await new Promise( (res, rej) => a.addEventListener("loaded", res, { once : true}));

						const anchorC = a.components.anchor;
						if(anchorC && anchorC.fits(slot))
						{
							if((data.anchorName || anchorC.data.name) && (data.anchorName != anchorC.data.name))
								continue;
							anchor = a;
							break;
						}
					}

					if(data.anchorName && !anchor) return console.warn("Anchor not found :", data.anchorName);

					if(slot && anchor)
					{
						console.log("Attaching Rrc-Entity to slot");
						//Stop listening
						el.removeEventListener("attached-to-slot", this.attachedListener);
						el.removeEventListener("detached-from-slot", this.detachedListener);
						//Attach directly without the attach-to-slot process : ignore locked
						slot.setAttribute("slot", { item : el, anchor : anchor });
						//Listen again
						el.addEventListener("attached-to-slot", this.attachedListener);
						el.addEventListener("detached-from-slot", this.detachedListener);
					}

					el.emit("rtc-entity-attached");
				});

				return;
			}
			else if( el.parentEl && el.parentEl.components.slot )
			{
				//Remove from parent slot.

				//Stop listening
				el.removeEventListener("attached-to-slot", this.attachedListener);
				el.removeEventListener("detached-from-slot", this.detachedListener);
				//Detach directly without the detach-from-slot process : ignore locked
				el.parentEl.setAttribute("slot", { item : null, anchor : null });
				//Listen again
				el.addEventListener("attached-to-slot", this.attachedListener);
				el.addEventListener("detached-from-slot", this.detachedListener);
			}
			
			return el.emit("rtc-entity-attached");
		}

		/* if( code == protocol.Attributes.code )
		{
			const data = client.deserialize(protocol.Attributes, payload) as AttributesPayload;
			console.dir({attributes : data});
			for(let attr of data.items)
			{
				const properties : { [key:string] : string } = {};
				for(let prop of attr.properties )
				{
					properties[ prop.property ] = prop.value;
				}
				el.setAttribute(attr.attribute, properties);
			}
			return;
		} */

		/* if( code == protocol.Bindings.code )
		{
			//const bindings : { [varName:string] : [] } = {};
			for(let count = reader.readByte(); --count >= 0; )
			{
				const targetVariable = reader.readString();
				const sourceGuid = reader.readGuid();
				const sourceVariable = reader.readString();

				this.system.getEntityAsync(sourceGuid).then((sourceEl) => 
				{
					this.el.setAttribute( "script__binding-" + targetVariable, {
						source : sourceEl,
						property : targetVariable,
						value : "source." + sourceVariable
					});
				} );
				
				/ *
					this.el.setAttribute( name ? ("binding__" + name) : "binding", targetEntity)
				);* /	
			}
			return;
		} */

		if( code == protocol.Variables.code )
		{
			const attributeValue : { [name:string] : any } = {};
			const context = 
			{
				//TODO : move logic to Protocol
				"IsNumber" : (obj : { type : number}) => (obj.type == variablesTypes.number),
				"IsGuid" : (obj : { type : number}) => (obj.type == variablesTypes.guid),
				"IsString" : (obj : { type : number}) => (obj.type == variablesTypes.string),
			};
			const data = client.deserialize(protocol.Variables, payload, context) as VariablesPayload;
			console.log("Receveiving entity variables", ...data.items.map(i => i.name));
			for(let item of data.items)
			{
				if(item.type == 0)
					attributeValue[item.name] = null;
				else if(item.type == variablesTypes.false)
					attributeValue[item.name] = false;
				else if(item.type == variablesTypes.true)
					attributeValue[item.name] = true;
				else if(item.type == variablesTypes.number)
					attributeValue[item.name] = item.number!;
				else if(item.type == variablesTypes.string)
					attributeValue[item.name] = new String( item.string! ); //Protect from parsing
				else if(item.type == variablesTypes.guid)
					attributeValue[item.name] = item.guid!;
			}
			this.variablesChangedListener && this.el.removeEventListener("variables-changed", this.variablesChangedListener);
			this.el.setAttribute("variables", attributeValue);
			this.variablesChangedListener && this.el.addEventListener("variables-changed", this.variablesChangedListener);
		}
	},

	/*
	playTrack(this: RtcEntityComponentInternal, track:MediaStreamTrack)
	{
		this.el.emit('play-media-track', track, true); //Handled by media-player system
	},


	// public method
	playLocalTrack(this: RtcEntityComponentInternal, track:MediaStreamTrack) : RtcEntityComponent
	{
		if(track.kind == "video") //Don't playback audio tracks.
			this.playTrack(track); //Play local stream ; remote instances will play the forwarded rtc video stream.

		const rtcClient = this.system.client!;
		
		//Add the local track to the connection, this will trigger a negociation
		rtcClient.addLocalTrack(track).then( (mid) =>
			//Once the track is added and its Mid is known, send a Play message
			rtcClient.sendObjectMessage(this.system.protocol!.Cast, this.data.id, { mid } )
		);

		return this;
	},
	*/



	attachTo(this : RtcEntityComponentInternal, slotEl : SlotEntity | null)
	{
		const rtcClient = this.system.client!;
		const slotC = slotEl && slotEl.components.slot;
		const slotName = slotC ? slotC.data.name : "";
		const anchorName = (slotC && slotC.data.anchor) ? slotC.data.anchor.components.anchor.data.name : "";
		let slotOwner = slotC ? myself : Guid.empty;
		for(let el : Entity | null | undefined = slotEl; el && (el !== el!.sceneEl); el = el!.parentEl)
		{
			const rtcX : { data : { id : Guid; }; } = (el.components["rtc-entity"] as RtcEntityComponent) || (el.components["rtc-avatar"] as RtcAvatarComponent);
			if(!rtcX) continue;
			slotOwner = rtcX.data.id;
			break;
		}

		const payload : AttachPayload = 
		{
			containerId : slotOwner,
			slotName : slotName,
			anchorName : anchorName || ""
		};
		rtcClient.sendObjectMessage(this.system.protocol!.Attach, this.data.id, payload);
		return this;
	},


	sendBindings(this : RtcEntityComponentInternal) : RtcEntityComponent
	{
		/*
		const payload : ByteOrByteArray[] = [];
		let count = 0;
		for(let key in this.el.components)
		{
			if(!key.startsWith("script__binding-"))
				continue;
				
			const scriptData = this.el.components[ key ].data as ScriptData;
			const targetVariable = scriptData.property;
			const sourceVariable = (scriptData.value && scriptData.value.startsWith("source.")) ? scriptData.value.slice(7) : "";
			const sourceEl = scriptData.source && scriptData.source[0];
			const sourceEntity = sourceEl && sourceEl.components["rtc-entity"] as RtcEntityComponent;
			if(sourceEntity && sourceEntity.data && sourceVariable && targetVariable)
			{
				++count;
				payload.push( encode(targetVariable), 0, sourceEntity.data.id.asBytes(), encode(sourceVariable), 0 );
			}
		}

		this.system.client!.sendMessage( this.system.protocol!.Bindings, this.data.id.asBytes(), count, ... payload );
		*/
		
		return this;
	},


	sendVariables(this : RtcEntityComponentInternal, changed : Variables) : RtcEntityComponent
	{
		const varComp = this.el.components["variables"];
		const varData = changed || (varComp && varComp.data as Variables) || {};
		
		const payload : VariablesPayload = { items : [] };

		for(let name in varData)
		{
			const value = varData[name];

			if(value === null)
				payload.items.push( { name, type : variablesTypes.null });
			else if(value === false)
				payload.items.push( { name, type : variablesTypes.false });
			else if(value === true)
				payload.items.push( { name, type : variablesTypes.true });
			else if(value instanceof Guid)
				payload.items.push( { name, type : variablesTypes.guid, guid : value });
			else
			{
				const type = typeof value;
				if(type === "number")
					payload.items.push( {name, type : variablesTypes.number, number : value as number });
				else if(type === "string")
					payload.items.push( {name, type : variablesTypes.string, string : value as string });
				else
					console.warn("Unsupported variable type", type);
			}
		}

		//console.log("Sending entity variables", ...payload.items.map(i => i.name));

		this.system.client!.sendObjectMessage( this.system.protocol!.Variables, this.data.id, payload );
		
		return this;
	},


	//public method
	sendTransform(this : RtcEntityComponentInternal ) : RtcEntityComponent
	{
		const obj = this.el.object3D;

		this.position.copy(obj.position);
		this.rotation.copy(obj.quaternion);

		
		const linV : Vector3Like = {x:0, y:0, z:0}, angV : Vector3Like = {x:0, y:0, z:0}
		
		const ammoBody = this.el.components["ammo-body"] as AmmoBodyComponent;
		if(ammoBody && ammoBody.body)
		{
			const lv = ammoBody.body.getLinearVelocity(), av = ammoBody.body.getAngularVelocity();
			linV.x = lv.x(); linV.y = lv.y(); linV.z = lv.z();
			angV.x = av.x(); angV.y = av.y(); angV.z = av.z();
		}

		const rtcClient = this.system.client!;

		const payload : TransformPayload = 
		{
			position : this.position,
			rotation : this.rotation,
			linearVelocity : linV,
			angularVelocity : angV
		};
		
		rtcClient.sendObjectMessage(this.system.protocol!.Transform, this.data.id, payload );

		return this;
	}

});